import React, { useMemo, useState } from "react";
import type { ReportsItemSalesData } from "src/features/Reports/types/report.types";
import type { ItemSalesRow } from "src/features/Reports/types/report.types";
import { BtnLoader } from "src/Loader/index";
import { formatCentsToDollarsWithDollarSign } from "src/helpers";
import { ReportBarChart } from "src/components/BarChart/BarChart";

const headerCellClass =
  "py-3.5 align-middle text-[11px] font-semibold uppercase tracking-[0.06em] text-[#374151] border-b border-[#e5e7eb] bg-[#fafbfc] whitespace-nowrap sticky top-0 z-10";
const numHeaderClass = `${headerCellClass} text-right`;
const numCellClass = "py-3 px-3 text-right tabular-nums text-sm whitespace-nowrap";
const rankCellClass = "py-3 pl-2 pr-2 text-right tabular-nums text-sm text-[#6b7280] w-9";

type SortBy = "revenue" | "quantity" | "name";
type Direction = "top" | "bottom";
type Limit = 10 | 25 | 50 | "all";

const SORT_OPTIONS: { value: SortBy; label: string }[] = [
  { value: "revenue", label: "Revenue" },
  { value: "quantity", label: "Quantity" },
  { value: "name", label: "Name" },
];

const LIMIT_OPTIONS: { value: Limit; label: string }[] = [
  { value: 10, label: "10" },
  { value: 25, label: "25" },
  { value: 50, label: "50" },
  { value: "all", label: "All" },
];

interface MenuPerformanceByItemTabProps {
  data: ReportsItemSalesData | null | undefined;
  loading: boolean;
  error: string | null;
  view?: "all";
}

function sortRows(rows: ItemSalesRow[], sortBy: SortBy, direction: Direction): ItemSalesRow[] {
  const sorted = [...rows].sort((a, b) => {
    if (sortBy === "revenue") {
      return (a.grossRevenueCents ?? 0) - (b.grossRevenueCents ?? 0);
    }
    if (sortBy === "quantity") {
      return (a.quantitySold ?? 0) - (b.quantitySold ?? 0);
    }
    return (a.itemName ?? "").localeCompare(b.itemName ?? "");
  });
  return direction === "top" ? sorted.reverse() : sorted;
}

export default function MenuPerformanceByItemTab({ data, loading, error }: MenuPerformanceByItemTabProps) {
  const [sortBy, setSortBy] = useState<SortBy>("revenue");
  const [direction, setDirection] = useState<Direction>("top");
  const [limit, setLimit] = useState<Limit>(10);

  const rows = useMemo(() => data?.rows ?? [], [data?.rows]);

  const displayedRows = useMemo(() => {
    const sorted = sortRows(rows, sortBy, direction);
    if (limit === "all") return sorted;
    return sorted.slice(0, limit);
  }, [rows, sortBy, direction, limit]);

  const totalQuantity = useMemo(() => rows.reduce((sum, r) => sum + (r.quantitySold ?? 0), 0), [rows]);
  const totalRevenueCents = useMemo(
    () => rows.reduce((sum, r) => sum + (r.grossRevenueCents ?? 0), 0),
    [rows]
  );
  const avgRevenuePerUnit =
    totalQuantity > 0 ? Math.round(totalRevenueCents / totalQuantity) : 0;
  const bestItem = useMemo(() => {
    if (rows.length === 0) return null;
    return rows.reduce((best, r) =>
      (r.grossRevenueCents ?? 0) > (best?.grossRevenueCents ?? 0) ? r : best
    );
  }, [rows]);

  const chartData = useMemo(() => {
    return displayedRows
      .slice(0, 10)
      .map((row) => ({
        name: row.itemName ?? "",
        value: row.grossRevenueCents != null ? row.grossRevenueCents / 100 : 0,
      }));
  }, [displayedRows]);

  const kpiItems: { label: string; value: React.ReactNode; valueClassName?: string; valueTitle?: string }[] = [
    {
      label: "Total Items Sold",
      value: loading ? "—" : totalQuantity.toLocaleString(),
    },
    {
      label: "Total Revenue",
      value: loading ? "—" : formatCentsToDollarsWithDollarSign(totalRevenueCents),
    },
    {
      label: "Avg per Unit",
      value: loading ? "—" : formatCentsToDollarsWithDollarSign(avgRevenuePerUnit),
    },
    {
      label: "Best Selling Item",
      value: loading ? "—" : bestItem?.itemName ?? "—",
      valueClassName: "truncate min-w-0",
      valueTitle: bestItem?.itemName ?? undefined,
    },
  ];

  const kpiRow = (items: typeof kpiItems, rowKey: string) => (
    <div className="grid grid-cols-2">
      {items.map((item, colIndex) => (
        <div
          key={`${rowKey}-${item.label}`}
          className={`py-1 min-w-0 ${colIndex > 0 ? "border-l border-[#e5e7eb] pl-4" : "pr-4"}`}
        >
          <p className="text-[10px] uppercase tracking-wider text-[#9ca3af] mb-0.5">{item.label}</p>
          <p
            className={`text-xl font-semibold text-[#111827] tabular-nums ${item.valueClassName ?? ""}`}
            title={item.valueTitle}
          >
            {item.value}
          </p>
        </div>
      ))}
    </div>
  );

  const kpiStrip = (
    <div className="mb-6 rounded-xl border border-[#e5e7eb] bg-[#fafafa] px-5 py-4">
      {kpiRow(kpiItems.slice(0, 2), "r1")}
      <div className="mt-4">{kpiRow(kpiItems.slice(2, 4), "r2")}</div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <BtnLoader />
      </div>
    );
  }

  return (
    <>
      {error && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
      )}
      {kpiStrip}
      {chartData.length > 0 && (
        <div className="rounded-[14px] bg-white pt-4 px-4 pb-6 border border-[#f1f1f1] shadow-[0_1px_2px_rgba(0,0,0,0.04)] mb-6 h-[360px]">
          <p className="text-xs text-[#9ca3af] mb-5">
            {direction === "top" ? "Top" : "Bottom"} 10 by {sortBy === "revenue" ? "revenue" : sortBy === "quantity" ? "quantity" : "name"}
          </p>
          <ReportBarChart
            data={chartData}
            dataKey="value"
            nameKey="name"
            color="#8fbfa8"
            horizontal
            isShowDollarSign
          />
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center gap-2">
          <span className="text-xs text-[#6b7280]">Sort by</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortBy)}
            className="text-sm rounded-md border border-[#e5e7eb] bg-white px-2.5 py-1.5 text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#FD7657]/30 focus:border-[#FD7657]"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-[#6b7280]">Show</span>
          <select
            value={direction}
            onChange={(e) => setDirection(e.target.value as Direction)}
            className="text-sm rounded-md border border-[#e5e7eb] bg-white px-2.5 py-1.5 text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#FD7657]/30 focus:border-[#FD7657]"
          >
            <option value="top">Top</option>
            <option value="bottom">Bottom</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-[#6b7280]">Limit</span>
          <select
            value={limit}
            onChange={(e) => setLimit(e.target.value === "all" ? "all" : Number(e.target.value) as Limit)}
            className="text-sm rounded-md border border-[#e5e7eb] bg-white px-2.5 py-1.5 text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#FD7657]/30 focus:border-[#FD7657]"
          >
            {LIMIT_OPTIONS.map((o) => (
              <option key={String(o.value)} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="w-full mt-4 bg-white rounded-xl border border-[#e5e7eb] shadow-[0_1px_3px_rgba(0,0,0,0.06)] overflow-x-auto">
        <table className="w-full min-w-[520px] border-collapse table-fixed">
            <colgroup>
              <col style={{ width: "2.25rem" }} />
              <col />
              <col style={{ width: "7.5rem" }} />
              <col style={{ width: "8.5rem" }} />
              <col style={{ width: "7.5rem" }} />
            </colgroup>
            <thead>
              <tr>
                <th className={`pl-2 pr-2 ${numHeaderClass} w-9`}>#</th>
                <th className={`pl-2 pr-4 ${headerCellClass} text-left`}>Item Name</th>
                <th className={`px-3 ${numHeaderClass}`}>Quantity Sold</th>
                <th className={`px-3 ${numHeaderClass}`}>Gross Revenue</th>
                <th className={`pr-4 pl-3 ${numHeaderClass}`}>% of Total Revenue</th>
              </tr>
            </thead>
            <tbody>
              {displayedRows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-10 text-center text-[#6b7280] text-sm">
                    No data for this period.
                  </td>
                </tr>
              ) : (
                displayedRows.map((row, i) => (
                  <tr
                    key={i}
                    className={`border-t border-[#f8fafc] text-[#111827] transition-colors hover:bg-[#f8fafc] ${i === 0 ? "bg-[#fafbfc]" : ""}`}
                  >
                    <td className={rankCellClass}>{i + 1}</td>
                    <td
                      className={`py-3 pl-2 pr-4 text-left align-middle break-words min-w-0 ${
                        i === 0 ? "font-semibold text-[#0f172a]" : i < 3 ? "font-semibold text-[#111827]" : ""
                      }`}
                    >
                      {row.itemName ?? "—"}
                    </td>
                    <td className={numCellClass}>{row.quantitySold ?? "—"}</td>
                    <td className={numCellClass}>{formatCentsToDollarsWithDollarSign(row.grossRevenueCents ?? 0)}</td>
                    <td className={numCellClass}>
                      {row.percentOfSales != null ? `${Number(row.percentOfSales).toFixed(1)}%` : "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
      </div>
    </>
  );
}
